import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { postReview, getReviewObject } from '../features/orders/orderSlice';
import Rating from 'react-rating';
import { GiSuperMushroom } from "react-icons/gi";
import { MdOutlineRateReview } from "react-icons/md";
import { FaTimes, FaPlus } from "react-icons/fa";
import { v4 } from "uuid";
import { app } from "../firebase";
import { ref, uploadBytesResumable, getStorage } from "firebase/storage";
import imageCompression from "browser-image-compression";
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { FaRegStar, FaStar } from "react-icons/fa";

function ReviewOrder() {
  const dispatch = useDispatch();
  const { reviewId } = useParams();
  const { review, isLoading, isError, message } = useSelector(
    (state) => state.order
  );

  const storage = getStorage(app);

  const [reviewCompleted, setReviewCompleted] = useState(false);
  const [modifiedReview, setModifiedReview] = useState(null);
  const [loading, setLoading] = useState(false);

  const fileInputRefs = useRef([]);

  useEffect(() => {
    if (reviewId) {
      dispatch(getReviewObject(reviewId));
    }
  }, [reviewId, dispatch]);

  useEffect(() => {
    if (review) {
      // Initialize the modifiedReview state with the fetched review
      setModifiedReview({
        ...review,
        product_reviews: review.product_reviews.map((productReview) => ({
          ...productReview,
          rating: productReview.rating || 0, // Default rating to 0 if not provided
          review: productReview.review || '', // Default review to an empty string if not provided
          images: productReview.images || [], // Initialize images array
        })),
      });
    }
  }, [review]);

  const handleRatingChange = (index, value) => {
    const updatedReviews = [...modifiedReview.product_reviews];
    updatedReviews[index].rating = value;
    setModifiedReview({ ...modifiedReview, product_reviews: updatedReviews });
  };

  const handleReviewChange = (index, value) => {
    const updatedReviews = [...modifiedReview.product_reviews];
    updatedReviews[index].review = value;
    setModifiedReview({ ...modifiedReview, product_reviews: updatedReviews });
  };

  const handleImageChange = (index, e) => {
    const files = Array.from(e.target.files);
    const updatedReviews = [...modifiedReview.product_reviews];
  
    // Check if adding these files exceeds the limit of 3 images
    if (updatedReviews[index].images.length + files.length > 3) {
      toast.warning('Var pievienot tikai 3 bildes.');
      // Only allow up to 3 images
      const allowedFiles = files.slice(0, 3 - updatedReviews[index].images.length);
      const newImages = allowedFiles.map((file) => {
        const url = URL.createObjectURL(file);
        return { src: url, name: file.name, file };
      });
      updatedReviews[index].images = [...updatedReviews[index].images, ...newImages];
    } else {
      const newImages = files.map((file) => {
        const url = URL.createObjectURL(file);
        return { src: url, name: file.name, file };
      });
      updatedReviews[index].images = [...updatedReviews[index].images, ...newImages];
    }
  
    setModifiedReview({ ...modifiedReview, product_reviews: updatedReviews });
  };

  const handleImageDelete = (reviewIndex, imageIndex) => {
    const updatedReviews = [...modifiedReview.product_reviews];
    updatedReviews[reviewIndex].images = updatedReviews[reviewIndex].images.filter(
      (_, i) => i !== imageIndex
    );
    setModifiedReview({ ...modifiedReview, product_reviews: updatedReviews });
  };

  const uploadImage = async (file) => {
    try {
      const imageName = v4();
      const maxWidthOrHeight = 800;

      const compressedFile = await imageCompression(file, {
        maxWidthOrHeight,
        useWebWorker: true,
      });

      const imageRef = ref(storage, `review_images/${imageName}`);
      const uploadTask = uploadBytesResumable(imageRef, compressedFile);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          null,
          (error) => {
            toast.error("Kaut kas nogāja greizi ar bildes augšupielādi");
            reject(error);
          },
          () => {
            // toast.success("Image uploaded successfully");
            resolve(imageName);
          }
        );
      });
    } catch (error) {
      toast.error("Kaut kas nogāja greizi ar bildes augšupielādi");
      throw error;
    }
  };

  const handleSubmit = async () => {
    const allReviewsAboveZero = modifiedReview.product_reviews.every(
      (productReview) => productReview.rating > 0
    );

    if (!allReviewsAboveZero) {
      toast.warning('Lūdzu ievadi vērtējumu visiem produktiem.');
      return;
    }

    setLoading(true);

    const updatedReviews = await Promise.all(
      modifiedReview.product_reviews.map(async (productReview) => {
        const uploadedImages = await Promise.all(
          productReview.images.map(async (image) => {
            if (image.file) {
              const imageName = await uploadImage(image.file);
              return imageName;
            }
            return image.name; // Keep existing image names
          })
        );

        return {
          ...productReview,
          images: uploadedImages,
        };
      })
    );

    const updatedReview = { ...modifiedReview, product_reviews: updatedReviews };

    // console.log("Updated Review:", updatedReview);
    dispatch(postReview({ review_data: updatedReview }));
    setLoading(false);
  };

  if(isLoading || !review){
    return <Spinner />
  }

  return (
    <div className='review-container'>
      <h2 className="light">Atsauksmes</h2>

      {review.completed ? (
        <p>Paldies par Jūsu atsaksmi!</p>
      ) : (
        <p>
          Paldies, ka atbalsti Nora Spora!
          <br />
          <br />
          Būsim ļoti pateicīgi, ja atstāsi vērtējumu par savu sēņu audzēšanas pieredzi!
        </p>
      )}

      {modifiedReview && (
        <div className="review-area">
          {modifiedReview.product_reviews.map((productReview, index) => (
            <div key={productReview._id} className="product-review flex column gap-5">
              <p>{productReview.product_name}</p>
              <label className="rating">
                Vērtējums:
                <Rating
                  emptySymbol={<FaRegStar size={20} color="#ffc000" />}
                  fullSymbol={<FaStar size={20} color="#ffc000" />}
                  initialRating={productReview.rating}
                  onChange={(value) => handleRatingChange(index, value)}
                />
              </label>
              <label>
                <textarea
                  placeholder="Atsauksme (nav obligāta)"
                  value={productReview.review}
                  onChange={(e) => handleReviewChange(index, e.target.value)}
                />
              </label>
              <div className="image-upload-section">
                <button
                  onClick={() => fileInputRefs.current[index].click()}
                  className="review-button add-images-button"
                  disabled={productReview.images.length >= 3} // Disable button if 3 images are already uploaded
                >
                  <FaPlus size={20} color="#ccc" />
                  <span className="light">Pievienot bildes</span>
                </button>
                <input
                  type="file"
                  multiple
                  ref={(el) => (fileInputRefs.current[index] = el)}
                  style={{ display: "none" }}
                  onChange={(e) => handleImageChange(index, e)}
                />
                <div className="image-gallery">
                  {productReview.images.map((image, imgIndex) => (
                    <div key={imgIndex} className="added-gallery-image">
                      <img src={image.src} alt="Review" className="product-image" />
                      <span
                        className="admin-remove-image"
                        onClick={() => handleImageDelete(index, imgIndex)}
                      >
                        <FaTimes />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {!review.completed && (
        <button onClick={handleSubmit} className="review-button review-submit">
          <MdOutlineRateReview size={20} color="#ccc" />
          <span className="light">Saglabāt</span>
        </button>
      )}
    </div>
  );
}

export default ReviewOrder;